import { useMemo } from "react"
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  CardProps,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported"

import { getIcon } from "utils/icon"
import { IMultiLanguageInfo } from "utils/feature"
import { useTranslation } from "providers/i18n"

export interface IProductDataCard {
  coverImage?: string
  name: IMultiLanguageInfo | string
}

export interface IProductCardProps extends CardProps {
  data: IProductDataCard
}

export const ProductCard: React.FC<IProductCardProps> = ({
  sx = {},
  data,
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const {
    root,
    cardMedia,
    fallbackCardMedia,
    cardContent,
    cardTitle,
    fallbackCardMediaIcon,
  } = theme.components?.[`ProductCard`]?.styleOverrides || {}
  const { name, coverImage } = data
  const fallbackIcon = useMemo(
    () =>
      getIcon(
        theme?.assets?.productModal?.fallbackIcon,
        fallbackCardMediaIcon,
        {
          iconCompProps: {
            className: "ProductCard-fallbackCardMediaIcon",
          },
        }
      ) || (
        <ImageNotSupportedIcon
          sx={fallbackCardMediaIcon}
          className="ProductCard-fallbackCardMediaIcon"
        />
      ),
    [theme, fallbackCardMediaIcon]
  )

  return (
    <Card sx={deepmerge(root, sx)} {...props}>
      <CardMedia sx={cardMedia} image={coverImage}>
        {!coverImage && <Box sx={fallbackCardMedia}>{fallbackIcon}</Box>}
      </CardMedia>
      <CardContent sx={cardContent}>
        <Typography sx={cardTitle}>{t(name)}</Typography>
      </CardContent>
    </Card>
  )
}
